import type { FunctionComponent, ReactElement } from 'react';

import { KeyCap } from '../../core/keyboard-shortcuts/KeyCap';
import { Tooltip } from '../../core/tooltip/Tooltip';

export type SideNavigationShortcut = {
    readonly keys: readonly string[];
    // Spoken instead of the key caps, such as "Shortcut: G, then B".
    readonly label: string;
};

// The open rail waits before a hint, so running the pointer down it stays quiet;
// the folded rail answers at once, since its icons carry no names.
const OPEN_RAIL_HINT_DELAY_MS = 500;

// A light pill beside the rail, its shadow alone setting it off, that fades in and out (side-navigation.css),
// never in the pointer's way, so running down the rows or a parent's stripes is
// never blocked by a hint over the next one. Its edge sits as far from the key caps at the sides as above
// them; the name pads its sides by the room its line height leaves above the letters, and stands as tall as a key cap,
// so a hint without a shortcut is as tall as one with it. Tooltip.Content joins classes
// without merging them, so each override is marked important.
const SIDE_NAVIGATION_TOOLTIP_CLASS =
    'side-nav-tooltip pointer-events-none flex items-center gap-m rounded-lg! border-0! p-xs! text-body-xs!';

export const spokenHint = (
    expanded: boolean,
    label: string,
    shortcut: SideNavigationShortcut | undefined,
): string | null => {
    if (shortcut === undefined) {
        return expanded ? null : label;
    }

    return expanded ? shortcut.label : `${label}, ${shortcut.label}`;
};

// The folded rail names the entry and adds its shortcut. The open rail already
// shows the name, so a hint there carries only the shortcut, and an entry
// without one has none. Kept mounted while it has nothing to show, so a row is
// not remounted when the rail folds, which would skip its label fade.
export const SideNavigationTooltip: FunctionComponent<{
    readonly children: ReactElement;
    readonly expanded: boolean;
    readonly label: string;
    readonly shortcut?: SideNavigationShortcut;
}> = ({ children, expanded, label, shortcut }) => {
    const spoken = spokenHint(expanded, label, shortcut);

    return (
        // A provider of its own, so the open rail's delay applies again on every row rather than
        // being skipped straight after a neighbour's hint. The hint ignores the pointer, so it holds no
        // grace area that would keep the next row's hint from opening.
        <Tooltip.Provider
            delayDuration={expanded ? OPEN_RAIL_HINT_DELAY_MS : 0}
            disableHoverableContent={true}
            skipDelayDuration={0}
        >
            <Tooltip open={spoken === null ? false : undefined}>
                <Tooltip.Trigger asChild={true}>{children}</Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content
                        aria-label={spoken ?? undefined}
                        className={SIDE_NAVIGATION_TOOLTIP_CLASS}
                        side="right"
                        sideOffset={10}
                        tone="floating"
                    >
                        {expanded ? null : <span className="flex h-5 items-center px-xs font-medium">{label}</span>}
                        {shortcut === undefined ? null : (
                            <span className="flex items-center gap-xxs">
                                {shortcut.keys.map((key, index) => (
                                    // biome-ignore lint/suspicious/noArrayIndexKey: a sequence may repeat a key, as in "G G"
                                    <KeyCap key={index}>{key}</KeyCap>
                                ))}
                            </span>
                        )}
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        </Tooltip.Provider>
    );
};
